import { apiClient } from './client'
import { ApiError } from './problem'
import type { EligibilityResultView, JudgmentGateView } from '../types/eligibility'

/**
 * `GET /api/v1/program-filings/{id}/eligibility-results` (docs/api-spec.md §10, IB-11, already
 * published — `EligibilityEvaluationController.listResults`). No additive backend route was needed
 * for this Case-workspace tab (GA demo lifecycle-tabs pass). A filing whose subjects have never
 * been evaluated returns an empty array (not an error) — the panel renders that as an honest empty
 * state, never a fabricated placeholder verdict.
 */
export async function listEligibilityResults(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<EligibilityResultView[]> {
  const { data } = await apiClient.get<EligibilityResultView[]>(
    `/program-filings/${programFilingId}/eligibility-results`,
    { signal },
  )
  return data
}

/**
 * `GET /api/v1/program-filings/{id}/judgment-gates` (docs/api-spec.md §10 "IB-11, narrowed" note,
 * already published — `EligibilityEvaluationController.listJudgmentGates`). Degrades a `422`
 * (`no_resolvable_eligibility_model` — the filing's `Program` has no published `EligibilityModel`
 * resolvable as-of today) to an empty array: that is a legitimate "nothing authored for this
 * program yet" state, not a fetch failure, so it does not surface as an error banner. Any other
 * failure still propagates as `ApiError` for the caller's normal error state.
 */
export async function listJudgmentGates(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<JudgmentGateView[]> {
  try {
    const { data } = await apiClient.get<JudgmentGateView[]>(
      `/program-filings/${programFilingId}/judgment-gates`,
      { signal },
    )
    return data
  } catch (err) {
    if (err instanceof ApiError && err.status === 422) {
      return []
    }
    throw err
  }
}
